/**
 * ChatSessionsSidebar — linke Spalte mit Liste aller Chats des Users (#1180).
 */
import { useState } from 'react';
import { MessageCircle, Plus, MoreVertical, Pencil, Archive, Trash2, Search } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

function relTime(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return lang === 'de' ? 'gerade' : 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(lang === 'de' ? 'de-AT' : 'en-US', { day: '2-digit', month: 'short' });
}

export default function ChatSessionsSidebar({ sessions, activeId, onSelect, onNew, onRename, onDelete, onArchive }) {
  const { t, language } = useLanguage();
  const [search, setSearch] = useState('');
  const [menuFor, setMenuFor] = useState(null);

  const filtered = (sessions || []).filter(s =>
    !search || (s.title || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <aside className="w-[260px] flex-shrink-0 bg-surface-elevated border-r border-line flex flex-col h-full">
      <div className="p-3 border-b border-line">
        <button
          onClick={onNew}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2a7bc4] text-white text-sm font-medium transition-colors"
        >
          <Plus size={16} />
          {t('chat.new_session') || 'Neuer Chat'}
        </button>
      </div>
      <div className="px-3 py-2 border-b border-line">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('chat.search_sessions') || 'Suchen…'}
            className="w-full pl-8 pr-2 py-1.5 text-sm rounded-[8px] border border-line bg-surface-input text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc]"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {filtered.length === 0 && (
          <div className="text-center py-8 text-sm text-[var(--text-tertiary)]">
            {t('chat.no_sessions') || 'Noch keine Unterhaltungen'}
          </div>
        )}
        {filtered.map((s) => {
          const isActive = String(s.id) === String(activeId);
          return (
            <div
              key={s.id}
              className={`group relative rounded-[8px] transition-colors ${
                isActive ? 'bg-[#338edc]/10' : 'hover:bg-surface-hover'
              }`}
            >
              <button
                onClick={() => onSelect?.(s.id)}
                className="w-full text-left px-2.5 py-2 flex items-start gap-2 min-w-0"
              >
                <MessageCircle
                  size={14}
                  className={`flex-shrink-0 mt-0.5 ${isActive ? 'text-[#338edc]' : 'text-[var(--text-tertiary)]'}`}
                />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm truncate ${isActive ? 'text-[#338edc] font-medium' : 'text-[var(--text-primary)]'}`}>
                    {s.title || t('chat.session_default_title') || 'Neue Unterhaltung'}
                  </div>
                  <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
                    {relTime(s.updated_at || s.created_at, language)}
                  </div>
                </div>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === s.id ? null : s.id); }}
                className="absolute right-1.5 top-1.5 p-1 rounded-[6px] opacity-0 group-hover:opacity-100 hover:bg-surface-input text-[var(--text-tertiary)]"
              >
                <MoreVertical size={14} />
              </button>
              {menuFor === s.id && (
                <div
                  className="absolute right-1.5 top-9 z-30 w-44 bg-surface-elevated border border-line rounded-[8px] shadow-elevated py-1"
                  onMouseLeave={() => setMenuFor(null)}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuFor(null); onRename?.(s); }}
                    className="w-full px-3 py-1.5 text-sm text-left hover:bg-surface-hover flex items-center gap-2 text-[var(--text-primary)]"
                  >
                    <Pencil size={14} /> {t('chat.rename') || 'Umbenennen'}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuFor(null); onArchive?.(s); }}
                    className="w-full px-3 py-1.5 text-sm text-left hover:bg-surface-hover flex items-center gap-2 text-[var(--text-primary)]"
                  >
                    <Archive size={14} /> {t('chat.archive') || 'Archivieren'}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuFor(null); onDelete?.(s); }}
                    className="w-full px-3 py-1.5 text-sm text-left hover:bg-surface-hover flex items-center gap-2 text-red-600 dark:text-red-400"
                  >
                    <Trash2 size={14} /> {t('chat.delete') || 'Löschen'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
